import React, { useState, useCallback } from "react";
import EmailTemplatePreview from "../EmailTemplatePreview";

// Status Toggle Component - Lives outside the form, saves immediately
const StatusToggle = ({ isActive, onToggle, isLoading, disabled }) => {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onToggle(!isActive)}
        disabled={isLoading || disabled}
        className={`
          relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2
          ${isActive ? 'bg-green-500' : 'bg-gray-200'}
          ${(isLoading || disabled) ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <span
          className={`
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
            transition duration-200 ease-in-out
            ${isActive ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </button>
      <span className={`text-sm font-medium ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
        {isLoading ? 'Saving...' : isActive ? 'Active' : 'Inactive'}
      </span>
    </div>
  );
};

// Visual Timeline Component for timing selection
const TimingSelector = ({ reminderType, daysBeforeOrAfter, onTypeChange, onDaysChange }) => {
  const isOverdue = reminderType === 'overdue';

  // Pre-defined timing options
  const timingPresets = isOverdue
    ? [1, 3, 5, 7, 14]
    : [1, 3, 5, 7, 14];

  return (
    <div className="space-y-4">
      {/* Type Selector - Visual Tabs */}
      <div className="flex rounded-lg bg-gray-100 p-1">
        <button
          type="button"
          onClick={() => onTypeChange('invoice')}
          className={`
            flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all
            ${!isOverdue
              ? 'bg-white text-indigo-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
            }
          `}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Before Due
        </button>
        <button
          type="button"
          onClick={() => onTypeChange('overdue')}
          className={`
            flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all
            ${isOverdue
              ? 'bg-white text-amber-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
            }
          `}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          After Due
        </button>
      </div>

      {/* Visual Timeline */}
      <div className={`rounded-xl p-4 ${isOverdue ? 'bg-amber-50 border border-amber-200' : 'bg-indigo-50 border border-indigo-200'}`}>
        <div className="flex items-center justify-center gap-4">
          {/* Timeline visualization */}
          <div className="flex items-center gap-2 text-sm">
            {!isOverdue && (
              <>
                <div className={`px-3 py-1.5 rounded-lg font-medium ${isOverdue ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800'}`}>
                  Email Sent
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <span>→</span>
                  <span className="text-xs">{daysBeforeOrAfter} days</span>
                  <span>→</span>
                </div>
              </>
            )}
            <div className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 font-medium">
              Due Date
            </div>
            {isOverdue && (
              <>
                <div className="flex items-center gap-1 text-gray-400">
                  <span>→</span>
                  <span className="text-xs">{daysBeforeOrAfter} days</span>
                  <span>→</span>
                </div>
                <div className={`px-3 py-1.5 rounded-lg font-medium ${isOverdue ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800'}`}>
                  Email Sent
                </div>
              </>
            )}
          </div>
        </div>

        {/* Sentence Description */}
        <p className={`text-center text-sm mt-3 ${isOverdue ? 'text-amber-700' : 'text-indigo-700'}`}>
          Send reminder <strong>{daysBeforeOrAfter} {daysBeforeOrAfter === 1 ? 'day' : 'days'}</strong> {isOverdue ? 'after' : 'before'} the invoice is due
        </p>
      </div>

      {/* Day Selector */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
          Select Timing
        </label>
        <div className="flex flex-wrap gap-2">
          {timingPresets.map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => onDaysChange(days)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${daysBeforeOrAfter === days
                  ? isOverdue
                    ? 'bg-amber-500 text-white'
                    : 'bg-indigo-500 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {days} {days === 1 ? 'day' : 'days'}
            </button>
          ))}
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="90"
              value={daysBeforeOrAfter}
              onChange={(e) => onDaysChange(parseInt(e.target.value) || 0)}
              className="w-20 px-3 py-2 rounded-lg border border-gray-200 text-sm text-center focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <span className="text-sm text-gray-500">custom</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Form Component
const EmailReminderEditForm = ({
  reminder,
  fundId,
  toggleStatusUrl,
  formAction,
  cancelUrl,
  removeAttachmentUrl,
  isNew,
  csrfToken,
  // Preview props
  fetchTemplateUrl,
  organizationName,
  fundName,
  supportEmail,
  sendTestUrl,
}) => {
  const [status, setStatus] = useState(reminder.status);
  const [isToggling, setIsToggling] = useState(false);
  const [name, setName] = useState(reminder.name || '');
  const [reminderType, setReminderType] = useState(reminder.reminder_type || 'invoice');
  const [daysBeforeOrAfter, setDaysBeforeOrAfter] = useState(reminder.days_before_or_after || 3);
  const [emailSubject, setEmailSubject] = useState(reminder.email_subject || '');
  const [message, setMessage] = useState(reminder.message || '');
  const [useCustomContent, setUseCustomContent] = useState(reminder.use_custom_content || false);
  const [errors, setErrors] = useState([]);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentRemoved, setAttachmentRemoved] = useState(false);

  // Compute current attachment state for preview
  const hasAttachment = attachmentFile ? true : (reminder.attachment && !attachmentRemoved);
  const attachmentFilename = attachmentFile ? attachmentFile.name : (reminder.attachment?.filename || '');

  const handleAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachmentFile(file);
      setAttachmentRemoved(false);
    }
  };

  const handleAttachmentRemove = () => {
    setAttachmentRemoved(true);
    setAttachmentFile(null);
  };

  const handleStatusToggle = useCallback(async (newActive) => {
    if (isNew) return; // Can't toggle status on new records

    setIsToggling(true);
    try {
      const response = await fetch(toggleStatusUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ status: newActive ? 'active' : 'inactive' }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus(data.status);
      } else {
        console.error('Failed to toggle status:', data.error);
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    } finally {
      setIsToggling(false);
    }
  }, [toggleStatusUrl, csrfToken, isNew]);

  return (
    <div className="space-y-6">
      {/* Header with Name and Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
                Reminder Name
              </label>
              <input
                type="text"
                name="scheduled_reminder[name]"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., 7-Day Payment Reminder"
                className="block w-full text-lg font-medium border-0 border-b-2 border-gray-200 focus:border-indigo-500 focus:ring-0 px-0 py-1 placeholder-gray-300"
              />
            </div>
            {!isNew && (
              <div className="flex-shrink-0 pt-5">
                <StatusToggle
                  isActive={status === 'active'}
                  onToggle={handleStatusToggle}
                  isLoading={isToggling}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timing Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">When to Send</h3>
          <p className="text-xs text-gray-500 mt-0.5">Choose when this reminder should be sent relative to the due date</p>
        </div>
        <div className="p-5">
          <TimingSelector
            reminderType={reminderType}
            daysBeforeOrAfter={daysBeforeOrAfter}
            onTypeChange={setReminderType}
            onDaysChange={setDaysBeforeOrAfter}
          />
          {/* Hidden fields for form submission */}
          <input type="hidden" name="scheduled_reminder[reminder_type]" value={reminderType} />
          <input type="hidden" name="scheduled_reminder[days_before_or_after]" value={daysBeforeOrAfter} />
        </div>
      </div>

      {/* Email Content Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Custom Content</h3>
            <p className="text-xs text-gray-500 mt-0.5">Override the default template subject and message</p>
          </div>
          <button
            type="button"
            onClick={() => setUseCustomContent(!useCustomContent)}
            className={`
              relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
              transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2
              ${useCustomContent ? 'bg-indigo-500' : 'bg-gray-200'}
            `}
          >
            <span
              className={`
                pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                transition duration-200 ease-in-out
                ${useCustomContent ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
        </div>

        {useCustomContent ? (
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
                Custom Subject Line
              </label>
              <input
                type="text"
                name="scheduled_reminder[email_subject]"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="e.g., Your payment is due soon"
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              />
              <p className="mt-1 text-xs text-gray-400">This will override the template's default subject</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
                Custom Message
              </label>
              <textarea
                name="scheduled_reminder[message]"
                id="scheduled_reminder_message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Write your custom message here..."
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              />
              <p className="mt-1 text-xs text-gray-400">
                This message will appear in the email instead of the default template message
              </p>
            </div>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Using default template content. Toggle on to customize the subject and message.</span>
            </div>
          </div>
        )}

        {/* Hidden field for use_custom_content */}
        <input type="hidden" name="scheduled_reminder[use_custom_content]" value={useCustomContent} />
        {/* Always include the fields even when hidden so they persist */}
        {!useCustomContent && (
          <>
            <input type="hidden" name="scheduled_reminder[email_subject]" value={emailSubject} />
            <input type="hidden" name="scheduled_reminder[message]" value={message} />
          </>
        )}
      </div>

      {/* Attachment Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Attachment</h3>
          <p className="text-xs text-gray-500 mt-0.5">Optionally attach a file to this reminder</p>
        </div>
        <div className="p-5">
          {/* Show existing attachment (not removed) or newly selected file */}
          {(reminder.attachment && !attachmentRemoved && !attachmentFile) && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{reminder.attachment.filename}</p>
                    <p className="text-xs text-gray-500">{reminder.attachment.size}</p>
                  </div>
                </div>
                {removeAttachmentUrl && (
                  <a
                    href={removeAttachmentUrl}
                    data-method="delete"
                    data-confirm="Are you sure you want to remove this attachment?"
                    className="text-sm font-medium text-red-600 hover:text-red-500"
                  >
                    Remove
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Show newly selected file */}
          {attachmentFile && (
            <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{attachmentFile.name}</p>
                    <p className="text-xs text-green-600">New file selected • Will be uploaded on save</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAttachmentFile(null)}
                  className="text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Remove
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-center w-full">
            <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              attachmentFile ? 'border-green-300 bg-green-50 hover:bg-green-100' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
            }`}>
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg className={`w-8 h-8 mb-3 ${attachmentFile ? 'text-green-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="mb-1 text-sm text-gray-500">
                  <span className="font-semibold">{attachmentFile ? 'Click to change file' : 'Click to upload'}</span> or drag and drop
                </p>
                <p className="text-xs text-gray-400">PDF, DOC, DOCX, or images (max 10MB)</p>
              </div>
              <input
                type="file"
                name="scheduled_reminder[attachment]"
                className="hidden"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif"
                onChange={handleAttachmentChange}
              />
            </label>
          </div>
          {(reminder.attachment && !attachmentRemoved && !attachmentFile) && (
            <p className="mt-2 text-xs text-gray-400 text-center">
              Upload a new file to replace the current attachment
            </p>
          )}
        </div>
      </div>

      {/* Hidden fields */}
      <input type="hidden" name="scheduled_reminder[delivery_method]" value="email" />
      <input type="hidden" name="scheduled_reminder[interval]" value="once" />
      {!isNew && <input type="hidden" name="scheduled_reminder[status]" value={status} />}

      {/* Email Preview Section */}
      {fetchTemplateUrl && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Email Preview</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {reminderType === 'overdue' ? 'Overdue reminder template' : 'Invoice reminder template'}
              </p>
            </div>
            {sendTestUrl && (
              <a
                href={sendTestUrl}
                data-method="post"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Test
              </a>
            )}
          </div>
          <div className="p-5">
            <EmailTemplatePreview
              fetchTemplateUrl={fetchTemplateUrl}
              reminderType={reminderType}
              organizationName={organizationName}
              fundName={fundName}
              supportEmail={supportEmail}
              message={useCustomContent ? message : ''}
              customSubject={useCustomContent ? emailSubject : ''}
              useCustomContent={useCustomContent}
              hasAttachment={hasAttachment}
              attachmentFilename={attachmentFilename}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <a
          href={cancelUrl}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          Cancel
        </a>
        <button
          type="submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {isNew ? 'Create Reminder' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// Wrapper that includes the form tag
const EmailReminderEditFormWrapper = (props) => {
  const { formAction, csrfToken, isNew } = props;

  return (
    <form
      action={formAction}
      method="post"
      encType="multipart/form-data"
      className="space-y-6"
    >
      <input type="hidden" name="authenticity_token" value={csrfToken} />
      {!isNew && <input type="hidden" name="_method" value="patch" />}
      <EmailReminderEditForm {...props} />
    </form>
  );
};

export default EmailReminderEditFormWrapper;
